<template>
  <div class="growth-pictures-page">
    <div class="toolbar">
      <el-button type="primary" icon="el-icon-plus" @click="handleAdd"
        >新增</el-button
      >
      <el-button
        type="danger"
        icon="el-icon-delete"
        :disabled="!selectedRows.length"
        @click="handleBatchDelete"
      >
        删除
      </el-button>
    </div>

    <el-table
      :data="pictureList"
      border
      class="custom-table"
      width="100%"
      height="520"
      v-loading="tableLoading"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="55" />
      <el-table-column
        label="排序"
        prop="sortOrder"
        width="90"
        align="center"
      />
      <el-table-column
        label="时期名称"
        prop="stageName"
        show-overflow-tooltip
      />
      <el-table-column label="时期图片" width="360" align="center">
        <template slot-scope="scope">
          <el-image
            v-if="scope.row.stageImage"
            :src="scope.row.stageImage"
            fit="cover"
            class="table-thumb"
            :preview-src-list="[scope.row.stageImage]"
          />
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="更新时间" prop="updateTime" />
      <el-table-column label="操作" width="160" align="center">
        <template slot-scope="scope">
          <el-button type="text" @click="handleView(scope.row)">查看</el-button>
          <el-button type="text" @click="handleEdit(scope.row)">编辑</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-wrapper">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="totalCount"
        :page-size="pageSize"
        :page-sizes="[10, 20, 30, 40]"
        :current-page="page"
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
      />
    </div>

    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="460px"
      @close="resetForm"
    >
      <el-form
        ref="pictureFormRef"
        :model="pictureForm"
        :rules="pictureRules"
        label-width="90px"
        class="picture-form"
      >
        <el-form-item label="排序" prop="sortOrder">
          <el-input-number
            v-model.number="pictureForm.sortOrder"
            :min="1"
            :step="1"
            :precision="0"
            :step-strictly="true"
            controls-position="right"
            placeholder="请输入序号"
            :disabled="isViewMode"
          />
        </el-form-item>
        <el-form-item label="时期名称" prop="stageName">
          <el-input
            v-model="pictureForm.stageName"
            placeholder="请输入"
            clearable
            :disabled="isViewMode"
          />
        </el-form-item>
        <el-form-item label="时期图片" prop="stageImage">
          <div class="upload-wrapper">
            <template v-if="isViewMode">
              <el-image
                v-if="pictureForm.stageImage"
                :src="pictureForm.stageImage"
                fit="cover"
                class="form-view-image"
                :preview-src-list="[pictureForm.stageImage]"
              />
              <span v-else class="empty-text">暂无图片</span>
            </template>
            <template v-else>
              <ImageUpload
                v-model="stageImageArray"
                :maxCount="1"
                accept=".png,.jpg,.jpeg"
                @uploading="handleImageUploading"
              />
              <div class="upload-tip">支持 png、jpg，最多可上传1张</div>
            </template>
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">{{
          isViewMode ? "关闭" : "取消"
        }}</el-button>
        <el-button v-if="!isViewMode" type="primary" @click="handleSave"
          >保存</el-button
        >
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  fetchGrowthStageImageList,
  addGrowthStageImage,
  editGrowthStageImage,
  getGrowthStageImageDetail,
  batchDeleteGrowthStageImage,
} from "@/api/growthStageImage";
import ImageUpload from "@/components/ImageUpload.vue";

export default {
  name: "GrowthPictures",
  components: {
    ImageUpload,
  },
  data() {
    return {
      pictureList: [],
      selectedRows: [],
      tableLoading: false,
      page: 1,
      pageSize: 20,
      totalCount: 0,
      dialogVisible: false,
      isViewMode: false,
      pictureForm: null,
      activeIndex: -1,
      stageImageArray: [],
      isImageUploading: false,
      formSubmitting: false,
      pictureRules: {
        sortOrder: [{ required: true, message: "请输入排序", trigger: "blur" }],
        stageName: [
          { required: true, message: "请输入时期名称", trigger: "blur" },
        ],
      },
    };
  },
  computed: {
    isEditing() {
      return Boolean(
        this.pictureForm && (this.pictureForm.id || this.pictureForm.id === 0)
      );
    },
    dialogTitle() {
      if (this.isViewMode) {
        return '查看详情';
      }
      return this.isEditing ? '编辑' : '新增';
    },
  },
  watch: {
    stageImageArray(newVal) {
      if (!this.pictureForm) return;
      const list = Array.isArray(newVal) ? newVal.filter(Boolean) : [];
      this.pictureForm.imageUrl = list;
      this.pictureForm.stageImage = list[0] || "";
    },
  },
  created() {
    this.pictureForm = this.getEmptyForm();
    this.fetchList();
  },
  methods: {
    async fetchList() {
      this.tableLoading = true;
      try {
        const res = await fetchGrowthStageImageList({
          page: this.page,
          pageSize: this.pageSize,
        });
        const { list = [], totalCount = 0 } = res.data || {};
        this.pictureList = list.map((item) => this.normalizeData(item));
        this.totalCount = parseInt(totalCount) || 0;
      } catch (error) {
        console.error("Failed to fetch growth stage image list", error);
        this.$message.error("获取列表失败");
      } finally {
        this.tableLoading = false;
      }
    },
    normalizeData(item = {}) {
      const stageImage = this.normalizeImageUrl(item.imageUrl);
      return {
        id: item.id ?? null,
        sortOrder: item.sortOrder ?? null,
        stageName: item.stageName ?? "",
        stageImage,
        imageUrl: this.normalizeImageList(item.imageUrl),
        updateTime: this.formatApiDate(item.updateTime),
      };
    },
    normalizeImageUrl(imageUrl) {
      if (Array.isArray(imageUrl) && imageUrl.length > 0) {
        const first = imageUrl[0];
        if (typeof first === "string") {
          return first;
        }
        if (first && typeof first === "object") {
          return first.url || first.imageUrl || first.path || "";
        }
      }
      if (typeof imageUrl === "string") {
        return imageUrl;
      }
      return "";
    },
    normalizeImageList(imageUrl) {
      if (Array.isArray(imageUrl)) {
        return imageUrl.filter(Boolean);
      }
      if (typeof imageUrl === "string" && imageUrl.trim()) {
        return [imageUrl.trim()];
      }
      if (imageUrl && typeof imageUrl === "object") {
        const url =
          imageUrl.url || imageUrl.imageUrl || imageUrl.path || imageUrl.src;
        return url ? [url] : [];
      }
      return [];
    },
    formatApiDate(value) {
      if (!value && value !== 0) {
        return "";
      }
      if (typeof value === "string") {
        return value;
      }
      if (typeof value === "number") {
        return this.formatTimestamp(value);
      }
      if (value && typeof value === "object") {
        if (value.time) {
          return this.formatTimestamp(value.time);
        }
        if (value.date) {
          const date = new Date(value.date);
          return Number.isNaN(date.getTime())
            ? ""
            : this.formatTimestamp(date.getTime());
        }
      }
      return "";
    },
    formatTimestamp(timestamp) {
      const date = new Date(timestamp);
      if (Number.isNaN(date.getTime())) {
        return "";
      }
      const yyyy = date.getFullYear();
      const mm = `${date.getMonth() + 1}`.padStart(2, "0");
      const dd = `${date.getDate()}`.padStart(2, "0");
      const hh = `${date.getHours()}`.padStart(2, "0");
      const mi = `${date.getMinutes()}`.padStart(2, "0");
      const ss = `${date.getSeconds()}`.padStart(2, "0");
      return `${yyyy}/${mm}/${dd} ${hh}:${mi}:${ss}`;
    },
    getEmptyForm() {
      return {
        id: null,
        sortOrder: null,
        stageName: "",
        stageImage: "",
        imageUrl: [],
        updateTime: "",
      };
    },
    handleAdd() {
      this.activeIndex = -1;
      this.isViewMode = false;
      this.pictureForm = this.getEmptyForm();
      this.stageImageArray = [];
      this.isImageUploading = false;
      this.dialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.pictureFormRef) {
          this.$refs.pictureFormRef.clearValidate();
        }
      });
    },
    async handleEdit(row) {
      const index = this.pictureList.indexOf(row);
      if (index === -1) return;
      this.activeIndex = index;
      this.isViewMode = false;
      if (!row.id && row.id !== 0) {
        this.$message.error("当前记录缺少ID，无法编辑");
        return;
      }
      try {
        const detail = await this.fetchDetail(row.id);
        this.pictureForm = { ...detail };
        this.stageImageArray = Array.isArray(detail.imageUrl)
          ? detail.imageUrl.filter(Boolean)
          : detail.stageImage
          ? [detail.stageImage]
          : [];
        this.isImageUploading = false;
        this.dialogVisible = true;
      } catch (error) {
        console.error("Failed to fetch detail", error);
        this.$message.error("获取详情失败");
      }
    },
    async handleView(row) {
      if (!row.id && row.id !== 0) {
        this.$message.error("当前记录缺少ID，无法查看");
        return;
      }
      try {
        const detail = await this.fetchDetail(row.id);
        this.isViewMode = true;
        this.pictureForm = { ...detail };
        this.stageImageArray = Array.isArray(detail.imageUrl)
          ? detail.imageUrl.filter(Boolean)
          : detail.stageImage
          ? [detail.stageImage]
          : [];
        this.dialogVisible = true;
      } catch (error) {
        console.error("Failed to fetch detail", error);
        this.$message.error("获取详情失败");
      }
    },
    async fetchDetail(id) {
      const res = await getGrowthStageImageDetail(id);
      return res.data || {};
    },
    handleSelectionChange(selection) {
      this.selectedRows = selection;
    },
    handleBatchDelete() {
      if (!this.selectedRows.length) return;
      const ids = this.selectedRows
        .map((row) => row.id)
        .filter((id) => id || id === 0);
      if (ids.length === 0) {
        this.$message.error("未找到有效的记录ID");
        return;
      }
      this.$confirm(`确认删除选中的 ${ids.length} 条记录？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(async () => {
          try {
            await batchDeleteGrowthStageImage(ids);
            this.$message.success("已删除");
            this.selectedRows = [];
            if (this.pictureList.length === ids.length && this.page > 1) {
              this.page -= 1;
            }
            this.fetchList();
          } catch (error) {
            console.error("Failed to delete", error);
            this.$message.error("删除失败");
          }
        })
        .catch(() => {});
    },
    handleSave() {
      if (this.formSubmitting) return;
      if (this.isImageUploading) {
        this.$message.warning("图片正在上传，请稍候...");
        return;
      }
      this.$refs.pictureFormRef.validate(async (valid) => {
        if (!valid) return;
        this.formSubmitting = true;
        try {
          const payload = {
            sortOrder: this.pictureForm.sortOrder,
            stageName: this.pictureForm.stageName,
            imageUrl: this.stageImageArray.filter(Boolean),
          };
          if (this.isEditing) {
            payload.id = this.pictureForm.id;
            await editGrowthStageImage(payload);
            this.$message.success("更新成功");
          } else {
            await addGrowthStageImage(payload);
            this.$message.success("新增成功");
          }
          this.dialogVisible = false;
          this.fetchList();
        } catch (error) {
          console.error("Failed to save", error);
          this.$message.error(this.isEditing ? "更新失败" : "新增失败");
        } finally {
          this.formSubmitting = false;
        }
      });
    },
    resetForm() {
      this.$nextTick(() => {
        if (this.$refs.pictureFormRef) {
          this.$refs.pictureFormRef.resetFields();
        }
        this.pictureForm = this.getEmptyForm();
        this.stageImageArray = [];
        this.isImageUploading = false;
        this.activeIndex = -1;
      });
    },
    handleImageUploading(status) {
      this.isImageUploading = status;
    },
    handlePageChange(page) {
      this.page = page;
      this.fetchList();
    },
    handleSizeChange(size) {
      this.pageSize = size;
      this.page = 1;
      this.fetchList();
    },
  },
};
</script>

<style scoped lang="less">
.growth-pictures-page {
  padding: 20px;
  box-sizing: border-box;
  background: #04070f;
  color: #e1e6f5;

  .toolbar {
    margin-bottom: 16px;

    .el-button + .el-button {
      margin-left: 12px;
    }
  }
}

.custom-table {
  background: transparent;
  table-layout: fixed;

  th {
    background: rgba(24, 30, 50, 0.95);
    color: #8fb1ff;
  }

  td {
    background: rgba(6, 9, 19, 0.95);
    color: #e9ecf7;
  }

  .table-thumb {
    width: 80px;
    height: 60px;
    border-radius: 4px;
    border: 1px solid #2f3650;
    object-fit: cover;
  }
}

.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  padding: 16px 0 0;
  box-sizing: border-box;
}

.picture-form {
  .upload-wrapper {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .upload-tip {
    color: #f56c6c;
    font-size: 12px;
  }
}

.detail-image {
  width: 200px;
  height: 140px;
  border-radius: 6px;
  border: 1px solid #2f3650;
  object-fit: cover;
}

.form-view-image {
  width: 200px;
  height: 140px;
  border-radius: 6px;
  border: 1px solid #2f3650;
  object-fit: cover;
  cursor: pointer;
}

.empty-text {
  color: #909399;
  font-size: 14px;
}
</style>
